// @ts-nocheck
import * as R from 'ramda'
import store from '@state/store'
import {
  computeWizardProgress,
  computeWizardStepsNeeded,
} from '@utils/wizardProgress'
import {
  Maybe,
  Wizard, // eslint-disable-line
} from '@domain'

export default [
  {
    path: '/',
    name: 'home',
    redirect: '/dataViz/choose-vis-type',
    component: () => lazyLoadView(import('@layouts/dataViz')),
  },
  {
    path: '/dataViz',
    name: 'dataViz',
    component: () => lazyLoadView(import('@layouts/dataViz')),
    redirect: '/dataViz/choose-vis-type',
    children: [
      {
        path: 'choose-vis-type',
        component: () => lazyLoadView(import('@views/progress/chooseVisType')),
      },
      {
        path: 'wizard',
        redirect: '/dataViz/wizard/breakdown',
        component: () => lazyLoadView(import('@views/progress/wizard')),
        beforeEnter: restoreWizardStateAndValidateRoute,
        children: [
          {
            path: 'breakdown',
            component: () => lazyLoadView(import('@views/progress/breakdown')),
          },
          {
            path: 'choose-single-region',
            component: () =>
              lazyLoadView(import('@views/progress/breakdown-instance')),
            props: {
              instanceName: 'Region',
              singleChoose: true,
            },
          },
          {
            path: 'choose-regions',
            component: () =>
              lazyLoadView(import('@views/progress/breakdown-instance')),
            props: {
              instanceName: 'Region',
              singleChoose: false,
            },
          },
          {
            path: 'choose-single-district',
            component: () =>
              lazyLoadView(import('@views/progress/breakdown-instance')),
            props: {
              instanceName: 'District',
              singleChoose: true,
            },
          },
          {
            path: 'choose-districts',
            component: () =>
              lazyLoadView(import('@views/progress/breakdown-instance')),
            props: {
              instanceName: 'District',
              singleChoose: false,
            },
          },
          {
            path: 'choose-campuses',
            component: () =>
              lazyLoadView(import('@views/progress/breakdown-instance')),
            props: {
              instanceName: 'Campus',
              singleChoose: false,
            },
          },
          {
            path: 'choose-data-categories',
            component: () =>
              lazyLoadView(import('@views/progress/choose-data-categories')),
          },
        ],
      },
      {
        path: 'reports',
        component: () => lazyLoadView(import('@views/chart/reports')),
        beforeEnter: restoreWizardStateAndValidateRoute,
      },
    ],
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

function checkThenGo(routePath) {
  return Maybe.case({
    Nothing: () => [],
    Just: _ => [
      {
        to: routePath,
      },
    ],
  })
}

function restoreWizardStateAndValidateRoute(to, from, next) {
  setTimeout(() => {
    store.commit('wizard/TryRestoreState')
    /** @type {Wizard} */
    let state = store.state.wizard

    state.chartType.map(cType => {
      store.dispatch('misc/SearchSchoolYears', cType)
    })

    let progresses = R.unnest([
      checkThenGo('/dataViz/wizard/breakdown')(state.chartType),
      computeWizardProgress(state),
      checkThenGo('/dataViz/wizard/choose-data-categories')(state.metrics),
    ])
    if (
      progresses.length > 1 &&
      progresses.length === computeWizardStepsNeeded(state) + 1
    ) {
      progresses.push({
        to: '/dataViz/reports',
      })
    }

    let safe = R.any(R.propEq('to', to.fullPath), progresses)
    if (safe) {
      next()
    } else {
      let lastSafeStand = progresses.pop()
      next({
        path: lastSafeStand ? lastSafeStand.to : '/dataViz/choose-vis-type',
        replace: true,
      })
    }
  })
}

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/loading').default,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/timeout').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
